import axios from 'axios'
import { KEYS, STORAGE, URLS } from '../../_config'
import { AccountInfo } from '@azure/msal-browser'
import { loginRequest } from '../../_config/authConfig'
import { trackError, trackAction } from '../../utils/analytics/rumTracking'
const axiosApiInstance = axios.create()

// axiosApiInstance.interceptors.response.use(
//     (response: any) => {
//         return response
//     },
//     async function (error: any) {
//         const originalRequest = error.config
//         if (error?.response?.status === 401 && !originalRequest._retry) {
//             originalRequest._retry = true
//             const access_token = await refreshAccessToken()
//             originalRequest.headers['Authorization'] = 'Bearer ' + access_token
//             return axiosApiInstance(originalRequest)
//         }
//         throw error instanceof Error ? error : new Error(error?.message ?? 'Unknown error')
//     },
// )

// AFTER
let refreshPromise: Promise<string | null> | null = null

axiosApiInstance.interceptors.response.use(
    (response: any) => {
        return response
    },
    async function (error: any) {
        const originalRequest = error.config
        if (error?.response?.status === 401 && !originalRequest._retry && !String(originalRequest?.url ?? '').includes('/negotiate')) {
            originalRequest._retry = true

            refreshPromise ??= refreshAccessToken().finally(() => {
                refreshPromise = null
            })

            const access_token = await refreshPromise
            originalRequest.headers['Authorization'] = 'Bearer ' + access_token
            return axiosApiInstance(originalRequest)
        }
        throw error instanceof Error ? error : new Error(error?.message ?? 'Unknown error')
    },
)

const refreshAccessToken = async () => {
    try {
        const MSALINSTANCE: any = globalThis.MSALINSTANCE
        const account: AccountInfo | null = MSALINSTANCE.getActiveAccount() || MSALINSTANCE.getAllAccounts()[0]
        const response = await MSALINSTANCE.acquireTokenSilent({
            scopes: [...loginRequest.scopes, 'openid', 'offline_access'], // Include offline_access for refresh token
            account,
            forceRefresh: true, // This forces MSAL to use the refresh token
        })
        saveToken(response.idToken, response.idToken)
        return response.idToken // Return the new token
    } catch (error) {
        localStorage.clear()
        console.error('Error acquiring token: MSALINSTANCE in window', error)
        trackError(error as Error, { source: 'token_refresh_failed' })
        return null // Return null on error
    }
}

function saveToken(accessToken: string, refreshToken: string) {
    localStorage.setItem(KEYS.AUTH_ACCESS, accessToken)
    localStorage.setItem(KEYS.AUTH_REFRESH, refreshToken)
}

function getToken() {
    return localStorage.getItem(KEYS.AUTH_ACCESS)
}

function getRefreshToken() {
    return localStorage.getItem(KEYS.AUTH_REFRESH)
}

const apiCatch = (error: any) => {
    if (error?.response?.status === 401 && globalThis.location.pathname != URLS.HOME && error?.config?.url.includes('/negotiate') === false) {
        localStorage.removeItem(STORAGE)
        trackAction('session_expired_redirect', { path: globalThis.location.pathname })
        globalThis.location.href = URLS.HOME
    }
}

export const responseHandler = (resp: any) => {
    if (resp.status >= 200 && resp.status < 300 && resp.data) {
        if (resp.data.data.exceptions?.length) {
            return { status: false, data: resp.data.data.exceptions.join(',') }
        } else {
            return { status: true, data: resp.data }
        }
    } else {
        return { status: false, data: resp.data }
    }
}

export class CallApi {
    static GET(url: any, params: any, cache: boolean = false, isAuth: boolean = false, isAdaptive: any = (e: any) => {}, genAIToken: string | null = null) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = {
                'Content-Type': 'application/json',
                AppHost: urls.hostname,
            }

            if (genAIToken) {
                headers['X-GenAI-Token'] = genAIToken
            }

            if (cache) {
                // Set headers to allow caching
                headers['Cache-Control'] = 'force-cache' // cache for 1 hour
            } else {
                headers['Cache-Control'] = 'no-cache'
            }
            if (isAuth || isAdaptive) {
                const token = getToken()
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                }
            }
            const config: any = { method: 'GET', url, headers, params }
            return axiosApiInstance(config)
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // return reject(error?.response?.data)
                    // ✅ FIXED: Preserve error.response.data structure for saga consumption
                    const errorData = error?.response?.data || {
                        message: error?.message || 'Unknown error occurred',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }
                    const enrichedError: any = new Error(errorData.message || 'API request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    trackError(enrichedError, { url, method: 'GET', status_code: enrichedError.status })
                    return reject(enrichedError)
                })
        })
    }

    static GETDOWNLOAD(url: any, params: any, isAuth: boolean = true, isAdaptive: any = false) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Pragma: 'no-cache',
                Expires: '0',
                'Content-Type': 'application/json',
                AppHost: urls.hostname,
            }
            if (isAuth || isAdaptive) {
                const token = getToken()
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                }
            }
            let config: any = { method: 'GET', url, headers, params }
            if (isAdaptive) {
                config = { method: 'GET', url, headers, params, responseType: 'arraybuffer' }
            }
            return axiosApiInstance(config)
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // ✅ FIXED: Preserve error structure
                    const errorData = error?.response?.data || {
                        message: error?.message || 'Download failed',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }
                    const enrichedError: any = new Error(errorData.message || 'Download request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    trackError(enrichedError, { url, method: 'GETDOWNLOAD', status_code: enrichedError.status })
                    return reject(enrichedError)
                })
        })
    }

    static POST(url: any, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => {}, genAIToken: string | null = null) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = {}
            const token = getToken()
            if (genAIToken) {
                headers['X-GenAI-Token'] = genAIToken
            }
            if (isAuth || token) {
                const token = getToken()
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                    headers.AppHost = urls.hostname
                }
            } else {
                headers.AppHost = urls.hostname
            }

            return axiosApiInstance
                .post(url, params, { headers, onUploadProgress: isAdaptive })
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // ✅ FIXED: Preserve error.response.data structure for saga consumption
                    // complete axios error in error.response
                    // API error response in error.response.data
                    const errorData = error?.response?.data || {
                        message: error?.message || 'POST request failed',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }

                    const enrichedError: any = new Error(errorData.message || 'API POST request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    trackError(enrichedError, { url, method: 'POST', status_code: enrichedError.status })
                    return reject(errorData?.error?.reserved_columns ? errorData : enrichedError)
                })
        })
    }

    static PATCH(url: any, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => {}, genAIToken: string | null = null) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = {}
            const token = getToken()
            if (genAIToken) {
                headers['X-GenAI-Token'] = genAIToken
            }
            if (isAuth || token) {
                const token = getToken()
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                    headers.AppHost = urls.hostname
                }
            } else {
                headers.AppHost = urls.hostname
            }

            return axiosApiInstance
                .patch(url, params, { headers, onUploadProgress: isAdaptive })
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // ✅ FIXED: Preserve error.response.data structure for saga consumption
                    const errorData = error?.response?.data || {
                        message: error?.message || 'PATCH request failed',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }
                    const enrichedError: any = new Error(errorData.message || 'API PATCH request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    return reject(enrichedError)
                })
        })
    }

    static PUT(url: string, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => {}) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = { 'Content-Type': 'application/json' }
            if (isAuth || isAdaptive) {
                const token = getToken()
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                    headers.AppHost = urls.hostname
                }
            } else {
                headers.AppHost = urls.hostname
            }
            return axiosApiInstance
                .put(url, params, { headers, onUploadProgress: isAdaptive })
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // ✅ FIXED: Preserve error.response.data structure for saga consumption
                    const errorData = error?.response?.data || {
                        message: error?.message || 'PUT request failed',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }
                    const enrichedError: any = new Error(errorData.message || 'API PUT request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    trackError(enrichedError, { url, method: 'PUT', status_code: enrichedError.status })
                    return reject(enrichedError)
                })
        })
    }

    static DELETE(url: string, params: any, isAuth: boolean = false, isAdaptive: any = (e: any) => {}) {
        return new Promise((resolve, reject) => {
            const urls = new URL(globalThis?.location?.href)
            const headers: any = { 'Content-Type': 'application/json' }
            const token = getToken()
            if (isAuth || token) {
                if (token) {
                    headers.Authorization = `Bearer ${token}`
                    headers.AppHost = urls.hostname
                }
            } else {
                headers.AppHost = urls.hostname
            }

            return axiosApiInstance
                .delete(url, {
                    headers,
                    onUploadProgress: isAdaptive,
                    data: params,
                })
                .then((success: any) => {
                    return resolve(success)
                })
                .catch((error: any) => {
                    apiCatch(error)
                    // return reject(error instanceof Error ? error : new Error(error.response ?? error.message ?? 'Unknown error'))
                    // ✅ FIXED: Preserve error.response.data structure for saga consumption
                    const errorData = error?.response?.data || {
                        message: error?.message || 'DELETE request failed',
                        status_code: error?.response?.status || 500,
                        success: false,
                    }
                    const enrichedError: any = new Error(errorData.message || 'API DELETE request failed')
                    enrichedError.data = errorData
                    enrichedError.status = errorData.status_code
                    trackError(enrichedError, { url, method: 'DELETE', status_code: enrichedError.status })
                    return reject(enrichedError)
                })
        })
    }
}
